import React, { useState, useEffect } from 'react';

// Type definitions
interface Disc {
  id: string;
  type: 'Distance Driver' | 'Fairway Driver' | 'Mid-range' | 'Putter';
  speed: number;
  glide: number;
  turn: number;
  fade: number;
  name: string;
  isUserDisc?: boolean;
  score?: number;
}

interface SkillLevel {
  level: string;
  maxDistance: number;
  speedCap: number;
  turnCap: number;
  description: string;
}

// Standard discs
const standardDiscs: Disc[] = [
  { id: 'd1', type: 'Distance Driver', speed: 13, glide: 5, turn: -1, fade: 3, name: '' },
  { id: 'd2', type: 'Distance Driver', speed: 12, glide: 5, turn: -1, fade: 2, name: '' },
  { id: 'd3', type: 'Distance Driver', speed: 11, glide: 5, turn: -1, fade: 3, name: '' },
  { id: 'd4', type: 'Distance Driver', speed: 10, glide: 5, turn: -2, fade: 2, name: '' },
  { id: 'd5', type: 'Distance Driver', speed: 9, glide: 5, turn: -3, fade: 1, name: '' },
  { id: 'f1', type: 'Fairway Driver', speed: 9, glide: 4, turn: 0, fade: 3, name: '' },
  { id: 'f2', type: 'Fairway Driver', speed: 8, glide: 5, turn: -1, fade: 2, name: '' },
  { id: 'f3', type: 'Fairway Driver', speed: 7, glide: 5, turn: -2, fade: 1, name: '' },
  { id: 'f4', type: 'Fairway Driver', speed: 7, glide: 4, turn: 0, fade: 3, name: '' },
  { id: 'f5', type: 'Fairway Driver', speed: 6, glide: 5, turn: -1, fade: 2, name: '' },
  { id: 'm1', type: 'Mid-range', speed: 5, glide: 5, turn: -1, fade: 1, name: '' },
  { id: 'm2', type: 'Mid-range', speed: 5, glide: 4, turn: 0, fade: 2, name: '' },
  { id: 'm3', type: 'Mid-range', speed: 4, glide: 5, turn: -1, fade: 1, name: '' },
  { id: 'm4', type: 'Mid-range', speed: 4, glide: 4, turn: 0, fade: 2, name: '' },
  { id: 'p1', type: 'Putter', speed: 3, glide: 3, turn: 0, fade: 1, name: '' },
  { id: 'p2', type: 'Putter', speed: 2, glide: 3, turn: 0, fade: 1, name: '' },
  { id: 'p3', type: 'Putter', speed: 1, glide: 2, turn: 0, fade: 1, name: '' }
];

// Popular discs list
const commonDiscs: Disc[] = [
  { id: 'i1', type: 'Distance Driver', speed: 13, glide: 5, turn: -1, fade: 3, name: 'Destroyer' },
  { id: 'i2', type: 'Distance Driver', speed: 12, glide: 5, turn: -1, fade: 3, name: 'Boss' },
  { id: 'i3', type: 'Distance Driver', speed: 12, glide: 5, turn: -2, fade: 2, name: 'Wraith' },
  { id: 'i4', type: 'Distance Driver', speed: 11, glide: 5, turn: -1, fade: 3, name: 'Teebird3' },
  { id: 'i5', type: 'Distance Driver', speed: 11, glide: 4, turn: 0, fade: 3, name: 'Firebird' },
  { id: 'i6', type: 'Fairway Driver', speed: 9, glide: 4, turn: -1, fade: 3, name: 'Thunderbird' },
  { id: 'i7', type: 'Fairway Driver', speed: 8, glide: 5, turn: -2, fade: 2, name: 'Valkyrie' },
  { id: 'i8', type: 'Fairway Driver', speed: 7, glide: 5, turn: -1, fade: 1, name: 'River' },
  { id: 'i9', type: 'Fairway Driver', speed: 7, glide: 5, turn: 0, fade: 2, name: 'Teebird' },
  { id: 'i10', type: 'Mid-range', speed: 5, glide: 5, turn: -1, fade: 1, name: 'Buzzz' },
  { id: 'i11', type: 'Mid-range', speed: 5, glide: 4, turn: 0, fade: 2, name: 'Roc3' },
  { id: 'i12', type: 'Mid-range', speed: 4, glide: 5, turn: -2, fade: 1, name: 'Mako3' },
  { id: 'i13', type: 'Putter', speed: 3, glide: 3, turn: 0, fade: 1, name: 'Aviar' },
  { id: 'i14', type: 'Putter', speed: 2, glide: 3, turn: 0, fade: 2, name: 'Judge' },
  { id: 'i15', type: 'Putter', speed: 2, glide: 4, turn: 0, fade: 1, name: 'Luna' },
  { id: 'i16', type: 'Putter', speed: 3, glide: 3, turn: 0, fade: 1, name: 'Reko' },
  { id: 'i17', type: 'Fairway Driver', speed: 10, glide: 4, turn: 0, fade: 2.5, name: 'Malm' },
  { id: 'i18', type: 'Fairway Driver', speed: 9, glide: 6, turn: -2, fade: 1, name: 'Falk' },
  { id: 'i19', type: 'Fairway Driver', speed: 6, glide: 5, turn: -2, fade: 1, name: 'Stig' }
];

const skillLevels: SkillLevel[] = [
  { level: 'Nybörjare', maxDistance: 60, speedCap: 7, turnCap: -1, description: '5/5' },
  { level: 'Medel', maxDistance: 80, speedCap: 9, turnCap: -2, description: '7/7' },
  { level: 'Avancerad', maxDistance: 100, speedCap: 11, turnCap: -3, description: '9/5' },
  { level: 'Expert', maxDistance: 120, speedCap: 12, turnCap: -4, description: '11/5' },
  { level: 'Pro', maxDistance: 160, speedCap: 14, turnCap: -5, description: '13/5' }
];

// Component props
interface WindDirectionIndicatorProps {
  direction: number;
  setDirection: React.Dispatch<React.SetStateAction<number>>;
}

interface PowerBarProps {
  power: number;
}

// Wind direction indicator component
const WindDirectionIndicator: React.FC<WindDirectionIndicatorProps> = ({ direction, setDirection }) => {
  const size = 150;
  const radius = size / 2 - 20;
  const centerX = size / 2;
  const centerY = size / 2;
  
  const angle = direction * Math.PI / 180;
  const markerX = centerX + radius * Math.sin(angle);
  const markerY = centerY - radius * Math.cos(angle);
  
  const handleClick = (e: React.MouseEvent<SVGSVGElement>) => {
    e.preventDefault();
    
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left - centerX;
    const y = e.clientY - rect.top - centerY;
    
    let angle = Math.atan2(x, -y) * 180 / Math.PI;
    if (angle < 0) angle += 360;
    
    setDirection(angle);
  };

  return (
    <div className="relative w-full max-w-md mx-auto">
      <svg width={size} height={size} onClick={handleClick} className="cursor-pointer mx-auto" onMouseDown={(e) => e.preventDefault()}>
        <circle cx={centerX} cy={centerY} r={radius} fill="transparent" stroke="#ccc" strokeWidth="1" />
        
        <text x={centerX} y={15} textAnchor="middle" fontSize="12" fill="#666">N (motvind)</text>
        <text x={size - 5} y={centerY} textAnchor="end" fontSize="12" fill="#666">Ö</text>
        <text x={centerX} y={size - 5} textAnchor="middle" fontSize="12" fill="#666">S (medvind)</text>
        <text x={5} y={centerY} textAnchor="start" fontSize="12" fill="#666">V</text>
        
        <line 
          x1={centerX} 
          y1={centerY} 
          x2={markerX} 
          y2={markerY} 
          stroke="#10b981" 
          strokeWidth="2" 
        />
        <circle cx={markerX} cy={markerY} r="6" fill="#10b981" />
      </svg>
    </div>
  );
};

// Power bar component
const PowerBar: React.FC<PowerBarProps> = ({ power }) => {
  const scaledPower = Math.ceil(power / 10);
  
  return (
    <div className="w-full max-w-xs mx-auto">
      <div className="flex items-center">
        <div className="text-sm mr-2 w-6">{scaledPower}/10</div>
        <div className="flex-1 h-6 bg-gray-200 rounded-full overflow-hidden">
          <div 
            className="h-full bg-green-500 rounded-full" 
            style={{ width: `${power}%` }}
          ></div>
        </div>
      </div>
    </div>
  );
};

// Main DiscGolfApp component
export default function DiscGolfApp() {
  const [myDiscs, setMyDiscs] = useState<Disc[]>([]);
  const [showAddDiscModal, setShowAddDiscModal] = useState(false);
  const [newDiscName, setNewDiscName] = useState('');
  const [newDiscType, setNewDiscType] = useState<Disc['type']>('Distance Driver');
  const [newDiscSpeed, setNewDiscSpeed] = useState(10);
  const [newDiscGlide, setNewDiscGlide] = useState(5);
  const [newDiscTurn, setNewDiscTurn] = useState(-1);
  const [newDiscFade, setNewDiscFade] = useState(3);
  const [showCommonDiscs, setShowCommonDiscs] = useState(false);
  const [selectedCommonDisc, setSelectedCommonDisc] = useState<Disc | null>(null);
  
  const [distance, setDistance] = useState(80);
  const [windDirection, setWindDirection] = useState(0);
  const [windStrength, setWindStrength] = useState(2);
  const [throwType, setThrowType] = useState<'Backhand' | 'Forehand'>('Backhand');
  const [skillLevel, setSkillLevel] = useState('Medel');
  const [courseCurve, setCourseCurve] = useState<'Rakt' | 'Vänster' | 'Höger'>('Rakt');
  const [throwAngle, setThrowAngle] = useState<'Upp' | 'Neutral' | 'Ned'>('Neutral');
  const [onlyMyDiscs, setOnlyMyDiscs] = useState(false);
  
  const [recommendedDisc, setRecommendedDisc] = useState<Disc | null>(null);
  const [almostMatchDiscs, setAlmostMatchDiscs] = useState<Disc[]>([]);
  const [throwPower, setThrowPower] = useState(80);

  // Only use localStorage on client side
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const savedDiscs = localStorage.getItem('myDiscs');
      if (savedDiscs) {
        setMyDiscs(JSON.parse(savedDiscs));
      } else {
        setMyDiscs([
          { id: 'md1', type: 'Distance Driver', speed: 11, glide: 5, turn: -1, fade: 3, name: 'Min Driver' },
          { id: 'md2', type: 'Mid-range', speed: 5, glide: 5, turn: -1, fade: 1, name: 'Min Midrange' },
          { id: 'md3', type: 'Putter', speed: 2, glide: 3, turn: 0, fade: 1, name: 'Min Putter' }
        ]);
      }
    }
  }, []);

  // Save discs to localStorage on client side
  useEffect(() => {
    if (typeof window !== 'undefined' && myDiscs.length > 0) {
      localStorage.setItem('myDiscs', JSON.stringify(myDiscs));
    }
  }, [myDiscs]);

  // Recommendation algorithm
  useEffect(() => {
    const selectedSkillLevel = skillLevels.find(s => s.level === skillLevel);
    if (!selectedSkillLevel) return;
    
    const maxSpeed = selectedSkillLevel.speedCap;
    const maxTurn = selectedSkillLevel.turnCap;
    
    const allDiscs = onlyMyDiscs
      ? myDiscs.map(disc => ({ ...disc, isUserDisc: true }))
      : [
          ...myDiscs.map(disc => ({ ...disc, isUserDisc: true })),
          ...commonDiscs.map(disc => ({ ...disc, isUserDisc: false }))
        ];
    
    const windDirectionFactor = Math.cos(windDirection * Math.PI / 180);
    const windEffect = windStrength * windDirectionFactor;
    
    const angleFactor = throwType === 'Backhand' 
      ? (throwAngle === 'Upp' ? 1 : throwAngle === 'Ned' ? -1 : 0)
      : (throwAngle === 'Upp' ? -1 : throwAngle === 'Ned' ? 1 : 0);
    
    const relativeDistance = distance / selectedSkillLevel.maxDistance;
    
    const scoredDiscs = allDiscs.map(disc => {
      let score = 10 - Math.abs(relativeDistance * 10 - disc.speed);
      
      if (disc.isUserDisc) {
        score += 1.5;
      }
      
      if (windEffect > 0) {
        score -= Math.abs(disc.fade - windEffect) * 0.5;
      } else if (windEffect < 0) {
        score -= Math.abs(disc.turn + windEffect) * 0.5;
      }
      
      if (throwType === 'Forehand') {
        score += disc.fade * 0.8;
        score -= Math.abs(disc.turn) * 0.4;
      } else {
        score += Math.abs(disc.turn) * 0.3;
      }
      
      if (throwAngle === 'Upp') {
        if (throwType === 'Backhand') {
          score += disc.fade * 0.7;
          score -= Math.abs(disc.turn) * 0.3;
        } else {
          score -= disc.fade * 0.3;
          score += Math.abs(disc.turn) * 0.7;
        }
      } else if (throwAngle === 'Ned') {
        if (throwType === 'Backhand') {
          score -= disc.fade * 0.3;
          score += Math.abs(disc.turn) * 0.7;
        } else {
          score += disc.fade * 0.7;
          score -= Math.abs(disc.turn) * 0.3;
        }
      }
      
      if (courseCurve === 'Vänster') {
        if (throwType === 'Backhand') {
          score += disc.fade * 2.5;
          score -= Math.abs(disc.turn) * 0.5;
          if (disc.fade < 2) {
            score -= 3;
          }
        } else {
          score += Math.abs(disc.turn) * 2.5;
          score -= disc.fade * 0.5;
          if (disc.turn > -1) {
            score -= 3;
          }
        }
      } else if (courseCurve === 'Höger') {
        if (throwType === 'Backhand') {
          score += Math.abs(disc.turn) * 2.5;
          score -= disc.fade * 0.5;
          if (disc.turn > -1) {
            score -= 3;
          }
        } else {
          score += disc.fade * 2.5;
          score -= Math.abs(disc.turn) * 0.5;
          if (disc.fade < 2) {
            score -= 3;
          }
        }
      } else {
        score += (5 - Math.abs(disc.turn) - disc.fade) * 0.8;
      }
      
      if (disc.speed > maxSpeed) {
        score -= (disc.speed - maxSpeed) * 2;
      }
      
      const distanceFactor = distance / 50;
      if (distanceFactor < 1 && disc.type === 'Putter') {
        score += 2;
      } else if (distanceFactor < 1.5 && disc.type === 'Mid-range') {
        score += 1.5;
      } else if (distanceFactor > 2 && disc.type === 'Distance Driver') {
        score += 1;
      }
      
      return { ...disc, score };
    });
    
    scoredDiscs.sort((a, b) => (b.score || 0) - (a.score || 0));
    
    if (scoredDiscs.length > 0) {
      setRecommendedDisc(scoredDiscs[0]);
      
      const bestScore = scoredDiscs[0].score || 0;
      const threshold = bestScore * 0.8;
      
      const almostMatches = scoredDiscs.slice(1).filter(d => (d.score || 0) >= threshold);
      setAlmostMatchDiscs(almostMatches);
      
      const maxPlayerDistance = selectedSkillLevel.maxDistance;
      let power = (distance / maxPlayerDistance) * 100;
      
      if (windEffect > 0) {
        power += windEffect * 6;
      } else {
        power += windEffect * 3;
      }
      
      const bestDisc = scoredDiscs[0];
      power += (bestDisc.speed - maxSpeed) * 3;
      
      power = Math.max(20, Math.min(100, power));
      setThrowPower(Math.round(power));
    }
  }, [distance, windDirection, windStrength, throwType, skillLevel, courseCurve, throwAngle, myDiscs, onlyMyDiscs]);

  const addDisc = () => {
    if (selectedCommonDisc) {
      const discToAdd = {
        ...selectedCommonDisc,
        id: `custom-${Date.now()}`,
        name: newDiscName || selectedCommonDisc.name
      };
      setMyDiscs([...myDiscs, discToAdd]);
    } else {
      const newDisc: Disc = {
        id: `custom-${Date.now()}`,
        type: newDiscType,
        speed: newDiscSpeed,
        glide: newDiscGlide,
        turn: newDiscTurn,
        fade: newDiscFade,
        name: newDiscName
      };
      setMyDiscs([...myDiscs, newDisc]);
    }
    
    setNewDiscName('');
    setNewDiscType('Distance Driver');
    setNewDiscSpeed(10);
    setNewDiscGlide(5);
    setNewDiscTurn(-1);
    setNewDiscFade(3);
    setSelectedCommonDisc(null);
    setShowAddDiscModal(false);
    setShowCommonDiscs(false);
  };

  const removeDisc = (id: string) => {
    setMyDiscs(myDiscs.filter(disc => disc.id !== id));
  };

  const selectCommonDisc = (disc: Disc) => {
    setSelectedCommonDisc(disc);
    setNewDiscType(disc.type);
    setNewDiscSpeed(disc.speed);
    setNewDiscGlide(disc.glide);
    setNewDiscTurn(disc.turn);
    setNewDiscFade(disc.fade);
    setNewDiscName(disc.name);
    setShowCommonDiscs(false);
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-lg mx-auto bg-white rounded-lg shadow-md overflow-hidden">
        <div className="bg-green-600 p-4 text-white">
          <h1 className="text-xl font-bold text-center">Diskgolf Rekommendation</h1>
        </div>
        
        {/* Settings */}
        <div className="p-4 border-b">
          <h2 className="text-lg font-medium mb-3">Kastinställningar</h2>
          
          <div className="space-y-4">
            {/* Distance */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Avstånd till korg: {distance} meter
              </label>
              <input 
                type="range" 
                min="10" 
                max="150" 
                value={distance} 
                onChange={(e) => setDistance(parseInt(e.target.value))} 
                className="w-full accent-green-500"
              />
            </div>
            
            {/* Wind conditions */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Vindriktning: {Math.round(windDirection)}°
              </label>
              <WindDirectionIndicator 
                direction={windDirection} 
                setDirection={setWindDirection} 
              />
              
              <label className="block text-sm font-medium text-gray-700 mt-3 mb-1">
                Vindstyrka: {windStrength}/5
              </label>
              <input 
                type="range" 
                min="0" 
                max="5" 
                value={windStrength} 
                onChange={(e) => setWindStrength(parseInt(e.target.value))} 
                className="w-full accent-green-500"
              />
            </div>
            
            {/* Throw type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Kastteknik:
              </label>
              <div className="flex space-x-2">
                <button 
                  className={`flex-1 py-2 px-4 rounded ${throwType === 'Backhand' ? 'bg-green-500 text-white' : 'bg-gray-200'}`}
                  onClick={() => setThrowType('Backhand')}
                >
                  Backhand
                </button>
                <button 
                  className={`flex-1 py-2 px-4 rounded ${throwType === 'Forehand' ? 'bg-green-500 text-white' : 'bg-gray-200'}`}
                  onClick={() => setThrowType('Forehand')}
                >
                  Forehand
                </button>
              </div>
            </div>
            
            {/* Course curve */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Önskad sväng på slutet:
              </label>
              <div className="flex space-x-2">
                <button 
                  className={`flex-1 py-2 px-2 rounded ${courseCurve === 'Vänster' ? 'bg-green-500 text-white' : 'bg-gray-200'}`}
                  onClick={() => setCourseCurve('Vänster')}
                >
                  Sväng vänster
                </button>
                <button 
                  className={`flex-1 py-2 px-2 rounded ${courseCurve === 'Rakt' ? 'bg-green-500 text-white' : 'bg-gray-200'}`}
                  onClick={() => setCourseCurve('Rakt')}
                >
                  Rakt fram
                </button>
                <button 
                  className={`flex-1 py-2 px-2 rounded ${courseCurve === 'Höger' ? 'bg-green-500 text-white' : 'bg-gray-200'}`}
                  onClick={() => setCourseCurve('Höger')}
                >
                  Sväng höger
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">Hur vill du att disken ska svänga på slutet av flygbanan</p>
            </div>
            
            {/* Throw angle */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Kastvinkel:
              </label>
              <div className="flex space-x-2">
                <button 
                  className={`flex-1 py-2 px-2 rounded ${throwAngle === 'Upp' ? 'bg-green-500 text-white' : 'bg-gray-200'}`}
                  onClick={() => setThrowAngle('Upp')}
                >
                  <div>Upp</div>
                  <div className="text-xs">(Hyzer)</div>
                </button>
                <button 
                  className={`flex-1 py-2 px-2 rounded ${throwAngle === 'Neutral' ? 'bg-green-500 text-white' : 'bg-gray-200'}`}
                  onClick={() => setThrowAngle('Neutral')}
                >
                  <div>Neutral</div>
                  <div className="text-xs">(Flat)</div>
                </button>
                <button 
                  className={`flex-1 py-2 px-2 rounded ${throwAngle === 'Ned' ? 'bg-green-500 text-white' : 'bg-gray-200'}`}
                  onClick={() => setThrowAngle('Ned')}
                >
                  <div>Ned</div>
                  <div className="text-xs">(Anhyzer)</div>
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">Vilken vinkel håller du discen vid release</p>
            </div>
            
            {/* Only my discs option */}
            <div className="flex items-center">
              <input 
                type="checkbox" 
                id="onlyMyDiscs" 
                checked={onlyMyDiscs} 
                onChange={(e) => setOnlyMyDiscs(e.target.checked)} 
                className="w-4 h-4 rounded border-gray-300 text-green-600"
              />
              <label htmlFor="onlyMyDiscs" className="ml-2 text-sm font-medium text-gray-700">
                Rekommendera endast från mina discar
              </label>
            </div>
            
            {/* Skill level */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Din nivå:
              </label>
              <select 
                value={skillLevel} 
                onChange={(e) => setSkillLevel(e.target.value)}
                className="w-full p-2 border rounded"
              >
                {skillLevels.map((level) => (
                  <option key={level.level} value={level.level}>
                    {level.level} ({level.description}) - max {level.maxDistance}m
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
        
        {/* Recommendation */}
        <div className="p-4 bg-gray-50">
          <h2 className="text-lg font-medium mb-3">Rekommenderad disc</h2>
          
          {recommendedDisc ? (
            <div className="bg-white p-4 rounded-lg shadow border-2 border-green-500">
              <div className="flex justify-between items-center">
                <div className="w-full">
                  <div className="flex justify-between items-center">
                    <h3 className="font-bold text-lg">
                      {recommendedDisc.name || `${recommendedDisc.type} (${recommendedDisc.speed}/${recommendedDisc.glide}/${recommendedDisc.turn}/${recommendedDisc.fade})`}
                    </h3>
                    {!recommendedDisc.isUserDisc && (
                      <button 
                        onClick={() => {
                          const discToAdd = {
                            ...recommendedDisc,
                            id: `custom-${Date.now()}`,
                            isUserDisc: true
                          };
                          setMyDiscs([...myDiscs, discToAdd]);
                        }}
                        className="text-xs bg-green-500 text-white px-2 py-1 rounded hover:bg-green-600"
                      >
                        Lägg till i mina discar
                      </button>
                    )}
                  </div>
                  <p className="text-gray-600">{recommendedDisc.type}</p>
                  <p className="mt-1">
                    <span className="inline-block bg-green-100 px-2 py-1 rounded text-sm">
                      Speed: {recommendedDisc.speed}
                    </span>
                    <span className="inline-block bg-green-100 px-2 py-1 rounded text-sm ml-1">
                      Glide: {recommendedDisc.glide}
                    </span>
                    <span className="inline-block bg-green-100 px-2 py-1 rounded text-sm ml-1">
                      Turn: {recommendedDisc.turn}
                    </span>
                    <span className="inline-block bg-green-100 px-2 py-1 rounded text-sm ml-1">
                      Fade: {recommendedDisc.fade}
                    </span>
                  </p>
                  <p className="mt-2 text-gray-600 text-sm">
                    {courseCurve !== 'Rakt' && (
                      <span>
                        <span className="font-medium">Sväng på slutet:</span> {courseCurve} • 
                      </span>
                    )}
                    <span className="font-medium">Kast:</span> {throwType} • 
                    <span className="font-medium">Vinkel:</span> {throwAngle} • 
                    <span className="font-medium">Avstånd:</span> {distance}m • 
                    <span className="font-medium">Vind:</span> {Math.round(windDirection)}° ({windStrength}/5)
                    {!recommendedDisc.isUserDisc && <span className="text-green-500 block mt-1">(Populär disc - ej från din samling)</span>}
                  </p>
                  
                  {/* Flight path visualization */}
                  <div className="mt-3">
                    <div className="text-sm font-medium text-gray-700 mb-1">Discens bana mot korgen (med kastvinkel):</div>
                    <div className="border rounded bg-green-50 h-20 p-1 relative overflow-hidden">
                      <svg viewBox="0 0 300 70" className="w-full h-full">
                        <path
                          d={
                            courseCurve === 'Vänster'
                              ? throwAngle === 'Upp' 
                                ? 'M 10,35 C 60,35 130,40 200,55 250,70 290,60 290,35'
                                : throwAngle === 'Ned'
                                ? 'M 10,35 C 60,35 120,30 200,35 250,50 290,40 290,35'
                                : 'M 10,35 C 70,35 150,35 200,50 250,65 290,45 290,35'
                              : courseCurve === 'Höger'
                              ? throwAngle === 'Upp'
                                ? 'M 10,35 C 60,35 120,40 200,35 250,20 290,30 290,35'
                                : throwAngle === 'Ned'
                                ? 'M 10,35 C 60,35 130,30 200,15 250,0 290,10 290,35'
                                : 'M 10,35 C 70,35 150,35 200,20 250,5 290,25 290,35'
                              : throwAngle === 'Upp'
                              ? 'M 10,35 C 70,35 150,38 200,40 250,42 290,38 290,35'
                              : throwAngle === 'Ned'
                              ? 'M 10,35 C 70,35 150,32 200,30 250,28 290,32 290,35'
                              : 'M 10,35 C 70,35 150,35 220,35 260,35 290,35 290,35'
                          }
                          fill="none"
                          stroke="#10b981"
                          strokeWidth="3"
                          strokeLinecap="round"
                        />
                        
                        <circle cx="10" cy="35" r="5" fill="#4b5563" />
                        <circle cx="290" cy="35" r="8" fill="none" stroke="#4b5563" strokeWidth="2" />
                        <line x1="282" y1="35" x2="298" y2="35" stroke="#4b5563" strokeWidth="2" />
                        
                        <text x="150" y="15" textAnchor="middle" fontSize="10" fill="#4b5563">
                          {courseCurve === 'Rakt' ? `${throwAngle} kastvinkel` : 
                           `${throwAngle} ${courseCurve.toLowerCase()}sväng`}
                        </text>
                        <text x="150" y="65" textAnchor="middle" fontSize="10" fill="#4b5563">
                          {throwType === 'Backhand' 
                            ? 'RHBH: ' 
                            : 'RHFH: '}
                          {courseCurve === 'Vänster' 
                            ? (throwType === 'Backhand' ? 'Använd överstabil disc (fade)' : 'Använd understabil disc (turn)')
                            : courseCurve === 'Höger'
                            ? (throwType === 'Backhand' ? 'Använd understabil disc (turn)' : 'Använd överstabil disc (fade)')
                            : 'Använd stabil disc (rakt)'}
                        </text>
                      </svg>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="mt-3">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Rekommenderad kraft:
                </label>
                <PowerBar power={throwPower} />
              </div>
            </div>
          ) : (
            <p className="text-gray-500 italic">Lägg till några discar för att få rekommendationer.</p>
          )}
        </div>
        
        {/* My discs */}
        <div className="p-4 border-t">
          <div className="flex justify-between items-center mb-3">
            <h2 className="text-lg font-medium">Mina discar</h2>
            <button 
              onClick={() => setShowAddDiscModal(true)}
              className="bg-green-500 text-white px-3 py-1 rounded text-sm"
            >
              Lägg till disc
            </button>
          </div>
          
          {myDiscs.length > 0 ? (
            <div className="space-y-2">
              {myDiscs
                .sort((a, b) => {
                  const typeOrder: Record<Disc['type'], number> = { 
                    'Distance Driver': 1, 
                    'Fairway Driver': 2, 
                    'Mid-range': 3, 
                    'Putter': 4 
                  };
                  const typeCompare = typeOrder[a.type] - typeOrder[b.type];
                  
                  if (typeCompare === 0) {
                    return b.speed - a.speed;
                  }
                  
                  return typeCompare;
                })
                .map((disc) => (
                  <div key={disc.id} className="flex justify-between items-center bg-white p-2 rounded border">
                    <div>
                      <div className="font-medium">
                        {disc.name || `${disc.type} (${disc.speed}/${disc.glide}/${disc.turn}/${disc.fade})`}
                      </div>
                      <div className="text-sm text-gray-600">
                        {disc.type} - {disc.speed}/{disc.glide}/{disc.turn}/{disc.fade}
                      </div>
                    </div>
                    <button 
                      onClick={() => removeDisc(disc.id)}
                      className="text-red-500 hover:text-red-700 text-sm"
                    >
                      Ta bort
                    </button>
                  </div>
                ))}
            </div>
          ) : (
            <p className="text-gray-500 italic">Du har inga discar ännu. Lägg till några!</p>
          )}
        </div>
        
        {/* Almost matches */}
        {almostMatchDiscs.length > 0 && (
          <div className="p-4 border-t">
            <details>
              <summary className="text-lg font-medium cursor-pointer">
                Andra bra alternativ ({almostMatchDiscs.length})
              </summary>
              <div className="mt-3 space-y-2">
                {almostMatchDiscs.map((disc) => (
                  <div key={disc.id} className="bg-white p-2 rounded border">
                    <div className="font-medium">
                      {disc.name || `${disc.type} (${disc.speed}/${disc.glide}/${disc.turn}/${disc.fade})`}
                    </div>
                    <div className="text-sm text-gray-600">
                      {disc.type} - {disc.speed}/{disc.glide}/{disc.turn}/{disc.fade}
                      {!disc.isUserDisc && <span className="text-green-500 ml-2">(Populär disc)</span>}
                    </div>
                  </div>
                ))}
              </div>
            </details>
          </div>
        )}
      </div>
      
      {/* Add disc modal */}
      {showAddDiscModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-medium mb-4">Lägg till ny disc</h3>
            
            {!showCommonDiscs ? (
              <div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Discnamn (valfritt)
                  </label>
                  <input 
                    type="text" 
                    value={newDiscName} 
                    onChange={(e) => setNewDiscName(e.target.value)} 
                    className="w-full p-2 border rounded"
                    placeholder="T.ex. Min Champion Destroyer"
                  />
                </div>
                
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Typ
                  </label>
                  <select 
                    value={newDiscType} 
                    onChange={(e) => setNewDiscType(e.target.value as Disc['type'])}
                    className="w-full p-2 border rounded"
                  >
                    <option value="Distance Driver">Distance Driver</option>
                    <option value="Fairway Driver">Fairway Driver</option>
                    <option value="Mid-range">Mid-range</option>
                    <option value="Putter">Putter</option>
                  </select>
                </div>
                
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Speed: {newDiscSpeed}
                    </label>
                    <input 
                      type="range" 
                      min="1" 
                      max="14" 
                      value={newDiscSpeed} 
                      onChange={(e) => setNewDiscSpeed(parseInt(e.target.value))} 
                      className="w-full accent-green-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Glide: {newDiscGlide}
                    </label>
                    <input 
                      type="range" 
                      min="1" 
                      max="7" 
                      value={newDiscGlide} 
                      onChange={(e) => setNewDiscGlide(parseInt(e.target.value))} 
                      className="w-full accent-green-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Turn: {newDiscTurn}
                    </label>
                    <input 
                      type="range" 
                      min="-5" 
                      max="1" 
                      value={newDiscTurn} 
                      onChange={(e) => setNewDiscTurn(parseInt(e.target.value))} 
                      className="w-full accent-green-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Fade: {newDiscFade}
                    </label>
                    <input 
                      type="range" 
                      min="0" 
                      max="5" 
                      value={newDiscFade} 
                      onChange={(e) => setNewDiscFade(parseInt(e.target.value))} 
                      className="w-full accent-green-500"
                    />
                  </div>
                </div>
                
                <div className="flex justify-between mb-4">
                  <button 
                    onClick={() => setShowCommonDiscs(true)}
                    className="bg-gray-200 text-gray-800 px-4 py-2 rounded"
                  >
                    Välj från populära discar
                  </button>
                  
                  <div className="flex space-x-2">
                    <button 
                      onClick={() => setShowAddDiscModal(false)}
                      className="bg-gray-200 text-gray-800 px-4 py-2 rounded"
                    >
                      Avbryt
                    </button>
                    <button 
                      onClick={addDisc}
                      className="bg-green-500 text-white px-4 py-2 rounded"
                    >
                      Lägg till
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div>
                <h4 className="text-md font-medium mb-2">Välj en populär disc</h4>
                <div className="max-h-60 overflow-y-auto border rounded p-2 mb-4">
                  {commonDiscs.map(disc => (
                    <div 
                      key={disc.id} 
                      className="p-2 hover:bg-green-50 cursor-pointer border-b"
                      onClick={() => selectCommonDisc(disc)}
                    >
                      <div className="font-medium">{disc.name}</div>
                      <div className="text-sm text-gray-600">
                        {disc.type} - {disc.speed}/{disc.glide}/{disc.turn}/{disc.fade}
                      </div>
                    </div>
                  ))}
                </div>
                <button 
                  onClick={() => setShowCommonDiscs(false)}
                  className="bg-gray-200 text-gray-800 px-4 py-2 rounded"
                >
                  Tillbaka
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
